import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Capacitor } from "@capacitor/core";

/**
 * GDPR cookie consent — first-visit banner. Choice is persisted to
 * localStorage; the "Cookie preferences" link in the footer resets it so users
 * can withdraw consent. Analytics (PostHog) reads this flag on load — see
 * /app/frontend/src/lib/analytics.js.
 *
 * NATIVE (iOS/Android via Capacitor): the banner does NOT render. Per Apple
 * Guideline 5.1.2(i), an app that shows a cookie/tracking prompt must use the
 * App Tracking Transparency framework. Since Binshare does not run any
 * third-party ad/cross-site tracking on the native shell (PostHog is
 * completely disabled in `analytics.js` when `Capacitor.isNativePlatform()`),
 * we simply don't need the banner there — no tracking, no consent needed.
 */
const STORAGE_KEY = "binshare_cookie_consent";       // "accepted" | "rejected"
const CHANGE_EVENT = "binshare-consent-change";      // dispatched on window when the choice updates

export function getConsent() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setConsent(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: value }));
  } catch {
    /* private-browsing / storage disabled — silently no-op */
  }
}

export function resetConsent() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: null }));
  } catch {
    /* no-op */
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Apple 5.1.2(i): never render the consent banner on native builds.
    if (Capacitor.isNativePlatform()) return;
    // First paint: hide by default, then check storage — avoids the banner
    // flashing during hydration on returning visitors.
    if (getConsent() === null) setVisible(true);

    // Support the footer "Cookie preferences" link opening this banner again.
    const onReset = () => {
      if (getConsent() === null) setVisible(true);
    };
    window.addEventListener(CHANGE_EVENT, onReset);
    return () => window.removeEventListener(CHANGE_EVENT, onReset);
  }, []);

  const choose = (value) => {
    setConsent(value);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      data-testid="cookie-banner"
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[9999]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="relative rounded-2xl bg-[#1B3624] text-white p-5 shadow-2xl border border-[#4A6B56]/30 overflow-hidden">
        {/* Terracotta glow — mirrors the WaitingListCallout so both feel like one brand */}
        <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-[#D67B54]/25 blur-2xl" aria-hidden />
        <button
          onClick={() => choose("rejected")}
          data-testid="cookie-banner-close"
          aria-label="Reject cookies"
          className="absolute top-3 right-3 text-white/60 hover:text-white transition"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="relative">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#D67B54] flex items-center justify-center shrink-0">
              <Cookie className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium" style={{ fontFamily: "Outfit" }}>
                Cookie preferences
              </div>
              <p className="text-xs text-white/75 mt-1 leading-relaxed">
                Strictly-necessary cookies keep you signed in. Analytics cookies (PostHog) help us understand what to improve. No advertising cookies, ever.{" "}
                <Link to="/privacy" className="underline hover:text-white">
                  Learn more
                </Link>
                .
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  data-testid="cookie-accept"
                  onClick={() => choose("accepted")}
                  className="flex-1 rounded-full bg-[#D67B54] hover:bg-[#c26b45] active:bg-[#b25f3d] px-4 py-2 text-xs font-medium transition"
                >
                  Accept all
                </button>
                <button
                  data-testid="cookie-reject"
                  onClick={() => choose("rejected")}
                  className="flex-1 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 px-4 py-2 text-xs font-medium transition"
                >
                  Reject non-essential
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
